import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { BookingFlow } from "@/components/BookingFlow";
import { Phone, MessageCircle, MapPin } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead } from "@/lib/seo";

const bookSearchSchema = z.object({
  service: z.string().max(60).optional(),
  area: z.string().max(60).optional(),
  context: z.string().max(80).regex(/^[a-z0-9-]+$/i).optional(),
  enquiry: z.string().max(500).optional(),
});

export const Route = createFileRoute("/book")({
  validateSearch: (search) => bookSearchSchema.parse(search),
  head: () => seoHead({
    path: "/book",
    title: "Request a Booking | MOT, Service & Repairs | Woodseer Autos",
    description: `Request your MOT, service, repair or diagnostics online — we call back during workshop hours. Or call ${NAP.phone} for the fastest reply. East London E14.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Request a Booking", path: "/book" },
    ],
  }),
  component: BookPage,
});

function BookPage() {
  const biz = getBusinessConfig();
  const { service, area, context } = Route.useSearch();

  return (
    <section className="py-12 md:py-20">
      <div className="mx-auto max-w-xl px-4 md:px-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Request a Booking</h1>
          <p className="mt-2 text-muted-foreground">
            Tell us what you need in under a minute. We'll call you back to confirm availability.
          </p>
          <p className="mt-2 text-sm"><Link to="/symptom-checker" className="font-semibold text-primary hover:underline">Not sure what you need? Try the symptom checker →</Link></p>
          {area && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-bold text-primary">
              <MapPin className="h-3 w-3" /> Visiting from {area}
            </p>
          )}
        </div>

        <BookingFlow preselectedService={service} preselectedArea={area} bookingContext={context} />

        <div className="mt-6 text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            Prefer to speak to someone?
          </p>
          <div className="flex items-center justify-center gap-3">
            <a href={biz.phoneHref} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
              <Phone className="h-3.5 w-3.5" />
              {biz.phone}
            </a>
            <a href={biz.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#25D366] hover:text-[#25D366]/80 transition-colors">
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
